import { EventClickArg, EventDropArg } from '@fullcalendar/core';
import { CalendarEventModel, CalendarEventType, EventStatusType } from '../../../../api-service/clients';
import { toDate } from '../../../../utilities/date-helper';
import type { PendingEventReschedule } from './calendar-event-types';
import type { ManageServiceProviderEventDialogEvent } from './dialogs/event-dialog-types';
import type { RecurrencePattern } from '../../../../components/calendar/recurrence/recurrence-pattern';

const DEFAULT_DURATION_IN_MINUTES = 60;

interface ResizedCalendarEventArg {
  event: EventClickArg['event'];
  oldEvent: EventClickArg['event'];
  revert: () => void;
}

function resolveDroppedEndAt(dropInfo: EventDropArg): Date {
  const newStartAt = dropInfo.event.start;
  if (!(newStartAt instanceof Date)) {
    return new Date(Date.now() + DEFAULT_DURATION_IN_MINUTES * 60 * 1000);
  }

  if (dropInfo.event.end instanceof Date) {
    return dropInfo.event.end;
  }

  const oldStartAt = dropInfo.oldEvent.start;
  if (dropInfo.oldEvent.end instanceof Date && oldStartAt instanceof Date) {
    const durationMs = dropInfo.oldEvent.end.getTime() - oldStartAt.getTime();
    return new Date(newStartAt.getTime() + durationMs);
  }

  return new Date(newStartAt.getTime() + DEFAULT_DURATION_IN_MINUTES * 60 * 1000);
}

function resolveResizedEndAt(resizeInfo: ResizedCalendarEventArg): Date {
  if (resizeInfo.event.end instanceof Date) {
    return resizeInfo.event.end;
  }

  if (resizeInfo.oldEvent.end instanceof Date) {
    return resizeInfo.oldEvent.end;
  }

  const eventStartAt = resizeInfo.event.start;
  if (eventStartAt instanceof Date) {
    return new Date(eventStartAt.getTime() + DEFAULT_DURATION_IN_MINUTES * 60 * 1000);
  }

  return new Date(Date.now() + DEFAULT_DURATION_IN_MINUTES * 60 * 1000);
}

function resolveOriginalEndAt(startAt: Date, endAt: Date | null): Date {
  if (endAt instanceof Date) {
    return endAt;
  }

  return new Date(startAt.getTime() + DEFAULT_DURATION_IN_MINUTES * 60 * 1000);
}

export function mapCalendarEventClickToManageDialogEvent(
  eventClickInfo: EventClickArg
): ManageServiceProviderEventDialogEvent | null {
  const eventType = eventClickInfo.event.extendedProps?.type;
  const eventId = eventClickInfo.event.extendedProps?.eventId;
  const canEdit = Boolean(eventClickInfo.event.extendedProps?.canEdit);
  const canCancel = Boolean(eventClickInfo.event.extendedProps?.canCancel);
  const canDelete = Boolean(eventClickInfo.event.extendedProps?.canDelete);

  if (eventType !== CalendarEventType.Event || !eventId || !canEdit) {
    return null;
  }

  const recurrence = eventClickInfo.event.extendedProps?.recurrence as RecurrencePattern | undefined;

  // A FullCalendar rrule-expanded occurrence has start/end pointing to the virtual occurrence
  // datetime while extendedProps.startDate holds the master anchor. Detect this by checking
  // whether the recurrence pattern is present (master events carry it; detached children don't).
  const isSeriesOccurrence = recurrence !== undefined && recurrence !== null;

  // For a recurring occurrence the master anchor is in extendedProps.startDate;
  // the clicked occurrence datetime is in eventClickInfo.event.start/end.
  const masterStartAt = isSeriesOccurrence
    ? (eventClickInfo.event.extendedProps?.startDate as Date | string | undefined)
    : undefined;
  const masterEndAt = isSeriesOccurrence
    ? (eventClickInfo.event.extendedProps?.endDate as Date | string | undefined)
    : undefined;

  const occurrenceStartAt = isSeriesOccurrence
    ? (eventClickInfo.event.start ?? undefined)
    : undefined;
  const occurrenceEndAt = isSeriesOccurrence
    ? (eventClickInfo.event.end ?? undefined)
    : undefined;

  // For whole-series edit the dialog form should be bound to the MASTER anchor so editing does
  // not overwrite dtstart. For occurrence edit the form pre-fills with the occurrence times.
  const dialogStartAt = masterStartAt ? new Date(masterStartAt) : (eventClickInfo.event.start ?? new Date());
  const dialogEndAt = masterEndAt ? new Date(masterEndAt) : (eventClickInfo.event.end ?? new Date());

  return {
    eventId,
    title: eventClickInfo.event.title ?? '',
    locationAddress: eventClickInfo.event.extendedProps?.locationAddress ?? '',
    startAt: dialogStartAt,
    endAt: dialogEndAt,
    allDay: eventClickInfo.event.allDay,
    content: eventClickInfo.event.extendedProps?.content,
    createdByUserId: eventClickInfo.event.extendedProps?.createdByUserId,
    eventStatus: eventClickInfo.event.extendedProps?.eventStatus as
      | EventStatusType
      | undefined,
    doneComment: eventClickInfo.event.extendedProps?.doneComment,
    cancelledComment: eventClickInfo.event.extendedProps?.cancelledComment,
    responsibleUserId:
      eventClickInfo.event.extendedProps?.responsibleUserId ??
      eventClickInfo.event.extendedProps?.createdByUserId ??
      '',
    consumedMinutes: eventClickInfo.event.extendedProps?.consumedMinutes,
    canCancel,
    canDelete,
    citizenName: eventClickInfo.event.extendedProps?.citizenName as string | undefined,
    citizenCpr: eventClickInfo.event.extendedProps?.citizenCpr as string | undefined,
    partialObjectiveId: eventClickInfo.event.extendedProps?.partialObjectiveId as string | undefined,
    observationTypeId: eventClickInfo.event.extendedProps?.observationTypeId as string | undefined,
    observationTypeName: eventClickInfo.event.extendedProps?.observationTypeName as string | undefined,
    citizenId: eventClickInfo.event.extendedProps?.citizenId as string | undefined,
    serviceProviderUnitId: eventClickInfo.event.extendedProps?.serviceProviderUnitId as string | undefined,
    recurrence,
    isSeriesOccurrence,
    occurrenceStartAt,
    occurrenceEndAt,
    noteTypeId: eventClickInfo.event.extendedProps?.noteTypeId as string | undefined,
  };
}

export function mapCalendarEventModelToManageDialogEvent(
  event: CalendarEventModel
): ManageServiceProviderEventDialogEvent | null {
  const startAt = toDate(event.startDate);
  const endAt = toDate(event.endDate);

  if (
    event.type !== CalendarEventType.Event ||
    !event.entityId ||
    !event.canEdit ||
    !startAt ||
    !endAt
  ) {
    return null;
  }

  return {
    eventId: event.entityId,
    title: event.title ?? '',
    locationAddress: event.locationAddress ?? '',
    startAt,
    endAt,
    allDay: event.allDay,
    content: event.content,
    createdByUserId: event.createdByUserId,
    eventStatus: event.eventStatus,
    doneComment: event.doneComment,
    cancelledComment: event.cancelledComment,
    responsibleUserId:
      event.responsibleUserId ?? event.createdByUserId ?? '',
    consumedMinutes: event.consumedMinutes,
    canCancel: event.canCancel,
    canDelete: event.canDelete,
    partialObjectiveId: event.partialObjectiveId,
    observationTypeId: event.observationTypeId,
    observationTypeName: event.observationTypeName,
    noteTypeId: event.noteTypeId,
  };
}

export function mapDropToPendingReschedule(
  dropInfo: EventDropArg
): PendingEventReschedule | null {
  const eventType = dropInfo.event.extendedProps?.type;
  const canEdit = Boolean(dropInfo.event.extendedProps?.canEdit);
  const eventId = dropInfo.event.extendedProps?.eventId as string | undefined;
  const responsibleUserId = dropInfo.event.extendedProps?.responsibleUserId as
    | string
    | undefined;
  const locationAddress = dropInfo.event.extendedProps?.locationAddress as
    | string
    | undefined;
  const originalStartAt = dropInfo.oldEvent.start;
  const newStartAt = dropInfo.event.start;

  if (
    eventType !== CalendarEventType.Event ||
    !canEdit ||
    !eventId ||
    !(originalStartAt instanceof Date) ||
    !(newStartAt instanceof Date)
  ) {
    return null;
  }

  const originalEndAt = resolveOriginalEndAt(originalStartAt, dropInfo.oldEvent.end);

  const recurrence = dropInfo.event.extendedProps?.recurrence as RecurrencePattern | undefined;
  const isSeriesOccurrence = recurrence !== undefined && recurrence !== null;

  return {
    revert: dropInfo.revert,
    actionType: 'move',
    eventId,
    title: dropInfo.event.title ?? '',
    locationAddress: locationAddress ?? '',
    responsibleUserId:
      responsibleUserId ??
      (dropInfo.event.extendedProps?.createdByUserId as string | undefined) ??
      '',
    originalAllDay: dropInfo.oldEvent.allDay,
    allDay: dropInfo.event.allDay,
    content: dropInfo.event.extendedProps?.content as string | undefined,
    consumedMinutes: dropInfo.event.extendedProps?.consumedMinutes as number | undefined,
    originalStartAt,
    originalEndAt,
    newStartAt,
    newEndAt: resolveDroppedEndAt(dropInfo),
    ...(isSeriesOccurrence
      ? {
          isSeriesOccurrence: true,
          seriesId: eventId,
          occurrenceStartAt: originalStartAt,
          masterStartAt: dropInfo.event.extendedProps?.startDate instanceof Date
            ? dropInfo.event.extendedProps.startDate
            : dropInfo.event.extendedProps?.startDate
              ? new Date(dropInfo.event.extendedProps.startDate as string)
              : undefined,
          masterEndAt: dropInfo.event.extendedProps?.endDate instanceof Date
            ? dropInfo.event.extendedProps.endDate
            : dropInfo.event.extendedProps?.endDate
              ? new Date(dropInfo.event.extendedProps.endDate as string)
              : undefined,
          recurrence,
        }
      : {}),
  };
}

export function mapResizeToPendingReschedule(
  resizeInfo: ResizedCalendarEventArg
): PendingEventReschedule | null {
  const eventType = resizeInfo.event.extendedProps?.type;
  const canEdit = Boolean(resizeInfo.event.extendedProps?.canEdit);
  const eventId = resizeInfo.event.extendedProps?.eventId as string | undefined;
  const responsibleUserId = resizeInfo.event.extendedProps?.responsibleUserId as
    | string
    | undefined;
  const locationAddress = resizeInfo.event.extendedProps?.locationAddress as
    | string
    | undefined;
  const originalStartAt = resizeInfo.oldEvent.start;
  const newStartAt = resizeInfo.event.start;

  if (
    eventType !== CalendarEventType.Event ||
    !canEdit ||
    !eventId ||
    !(originalStartAt instanceof Date) ||
    !(newStartAt instanceof Date)
  ) {
    return null;
  }

  const originalEndAt = resolveOriginalEndAt(
    originalStartAt,
    resizeInfo.oldEvent.end
  );

  const recurrence = resizeInfo.event.extendedProps?.recurrence as RecurrencePattern | undefined;
  const isSeriesOccurrence = recurrence !== undefined && recurrence !== null;

  return {
    revert: resizeInfo.revert,
    actionType: 'resize',
    eventId,
    title: resizeInfo.event.title ?? '',
    locationAddress: locationAddress ?? '',
    responsibleUserId:
      responsibleUserId ??
      (resizeInfo.event.extendedProps?.createdByUserId as string | undefined) ??
      '',
    originalAllDay: resizeInfo.oldEvent.allDay,
    allDay: resizeInfo.event.allDay,
    content: resizeInfo.event.extendedProps?.content as string | undefined,
    consumedMinutes: resizeInfo.event.extendedProps?.consumedMinutes as number | undefined,
    originalStartAt,
    originalEndAt,
    newStartAt,
    newEndAt: resolveResizedEndAt(resizeInfo),
    ...(isSeriesOccurrence
      ? {
          isSeriesOccurrence: true,
          seriesId: eventId,
          occurrenceStartAt: originalStartAt,
          masterStartAt: resizeInfo.event.extendedProps?.startDate instanceof Date
            ? resizeInfo.event.extendedProps.startDate
            : resizeInfo.event.extendedProps?.startDate
              ? new Date(resizeInfo.event.extendedProps.startDate as string)
              : undefined,
          masterEndAt: resizeInfo.event.extendedProps?.endDate instanceof Date
            ? resizeInfo.event.extendedProps.endDate
            : resizeInfo.event.extendedProps?.endDate
              ? new Date(resizeInfo.event.extendedProps.endDate as string)
              : undefined,
          recurrence,
        }
      : {}),
  };
}
